import React, { useEffect, useState } from "react";
import { Image, KeyboardAvoidingView, Platform, ScrollView, StyleSheet, View } from "react-native";
import Button from "theme/Button";
import Text from "theme/Text";
import i18n from "utils/i18n";
import { layoutStyles } from "styles/layout";
import { useDispatch, useSelector } from "react-redux";
import _ from 'lodash';
import UserService from "services/user";
import { resetData } from 'services/redux/data/actions'
import { resetAuthentication } from 'services/redux/authentication/actions'
import { resetCart } from 'services/redux/cart/actions'
import { resetManagement } from 'services/redux/management/actions'
import AppUpdater from 'services/app-updater'
import { colorList } from 'utils/colors'
import { authenticationConstants } from 'services/redux/authentication/constants'
import VStack from 'components/atoms/stack/VStack'
import Token from 'scenes/authentication/components/Token'
import { IconBackground } from 'components/atoms/IconBackground'
import iconImage from 'assets/images/icon.png'
import ThemedBlock from 'components/atoms/ThemedBlock'
import { styles as authenticationStyles } from 'scenes/authentication/styles/styles'
import { useIsSmallDevice } from 'utils/helper-device'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import HelperVersion from 'utils/helper-version'
import HelperEncryption from 'utils/helper-encryption'

const LoginCheckError = ({ action, logoutCallback, error }) => {
	
	const dispatch = useDispatch()
	const isSmallDevice = useIsSmallDevice()
	const insets = useSafeAreaInsets()
	const _tokenRevalidate = useSelector( state => {
		return state.authentication[authenticationConstants.TOKEN_REVALIDATE];
	} )
	
	const [ retrying, setRetrying ] = useState( false )
	
	const retryCallback = () => {
		setRetrying( false )
	}
	
	const onRetry = () => {
		if( action ) {
			setRetrying( true )
			
			action( { callback: retryCallback } );
		}
	}
	
	const onLogout = () => {
		UserService.logout({
			resetData: () => {
				return dispatch(resetData());
			},
			resetManagement: () => {
				return dispatch(resetManagement());
			},
			resetAuthentication: () => {
				return dispatch(resetAuthentication());
			},
			resetCart: () => {
				return dispatch(resetCart());
			},
		});
		
		if( logoutCallback && _.isFunction( logoutCallback ) ) {
			logoutCallback();
		}
	}
	
	const checkForUpdates = () => {
		const appUpdater = new AppUpdater();
		
		appUpdater
			.check()
			.then( () => {
				if( appUpdater.updateIsAvailable ) {
					appUpdater.createAlert();
				}
			});
	}
	
	useEffect( () => {
		checkForUpdates()
		
		// When we have to re validate, we do not KNOW the user and we want to reset the encryption key
		if( _tokenRevalidate === true ) {
			HelperEncryption.resetKey()
		}
	}, [] )
	
	return (
		<KeyboardAvoidingView
			style={ layoutStyles.flexOne }
			behavior={ Platform.OS === 'ios' ? 'padding' : 'height' }>
			<ScrollView
				style={ layoutStyles.flexOne }
				contentContainerStyle={ styles.content }
				keyboardShouldPersistTaps="handled">
				<IconBackground
					containerStyle={ layoutStyles.flexOne }
					iconImageStyle={ layoutStyles.flexOne }
					opacity={ 0.1 }
					linearGradientColours={[
						colorList.PAGE.primary,
						colorList.ORANGE.subtle,
						colorList.PAGE.primary,
						colorList.WHITE.primary
					]}>
					<View style={[
						authenticationStyles.outerContainer,
						isSmallDevice ? authenticationStyles.outerContainerMobile : null,
						{ paddingTop: Math.max( insets.top, isSmallDevice ? 16 : 32 ) }
					]}>
						<View style={ authenticationStyles.headerContainer }>
							<Image source={ iconImage } style={ authenticationStyles.headerImage } resizeMode="contain" />
							<Text category="h1" style={ authenticationStyles.headerTitle }>
								{ i18n.t('page.authentication.domain.title') }
							</Text>
							<Text style={ authenticationStyles.headerSubtitle }>
								{ i18n.t('page.authentication.domain.subtitle') }
							</Text>
						</View>

						<View style={ authenticationStyles.container }>
							<ThemedBlock mediumPadding style={[
								authenticationStyles.authenticationCard,
								isSmallDevice ? authenticationStyles.authenticationCardMobile : null
							]}>
								<VStack>
									<Text category="h4" style={ authenticationStyles.headerTitleDark }>
										{ i18n.t( _tokenRevalidate ? 'page.authentication.domain.token' : 'error.global.title' ) }
									</Text>
									{ _tokenRevalidate === true ? (
										<Token reValidate={ true } />
									) : (
										<>
											<Text style={ styles.text }>
												{ i18n.t('error.error_login.text') }
											</Text>
											<Button
												style={ authenticationStyles.domainContinueButton }
												isLoading={ retrying }
												disabled={ retrying }
												onPress={ onRetry }>
												{ i18n.t('error.retry_button') }
											</Button>
											<Button appearance="ghost" onPress={ onLogout }>
												{ i18n.t('error.error_login.button_logout') }
											</Button>
										</>
									)}
								</VStack>
							</ThemedBlock>
						</View>
					</View>
					<View style={{ paddingBottom: insets.bottom }}>
						<Text style={ authenticationStyles.modVersion }>
							{ i18n.t('app_name') } { HelperVersion.get() }
						</Text>
					</View>
				</IconBackground>
			</ScrollView>
		</KeyboardAvoidingView>
	);
}

const styles = StyleSheet.create({
	content: {
		flexGrow: 1,
		backgroundColor: colorList.PAGE.primary,
	},
	text: {
		color: colorList.TEXT.light,
		lineHeight: 22,
	},
});

export default LoginCheckError
